import '../../i18n/autoTranslate';
import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressTracker = ({ 
  userProgress,
  className = "" 
}) => {
  const progressStats = [
    {
      id: 'completed',
      label: 'Completed',
      value: userProgress?.completed || 0,
      total: userProgress?.total || 0,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 'inProgress',
      label: 'In Progress',
      value: userProgress?.inProgress || 0,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      id: 'streak',
      label: 'Current Streak',
      value: userProgress?.streak || 0,
      unit: 'days',
      icon: 'Flame',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 'totalTime',
      label: 'Total Time',
      value: userProgress?.totalTime || 0,
      unit: 'hrs',
      icon: 'Timer',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    }
  ];

  const completionPercentage = userProgress?.total > 0 
    ? Math.round((userProgress?.completed / userProgress?.total) * 100) 
    : 0;

  return (
    <div className={`rounded-3xl border border-white/10 bg-white/5 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} className="text-white/80" />
          <h3 className="text-lg font-semibold text-white">Your Progress</h3>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-white/60">
          <Icon name="Calendar" size={16} />
          <span>This Week</span>
        </div>
      </div>
      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white/80">Overall Completion</span>
          <span className="text-sm font-bold text-white">{completionPercentage}%</span>
        </div>
        
        <div className="w-full bg-white/5 rounded-full h-3 border border-white/10 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF] h-3 rounded-full transition-all duration-500 ease-smooth relative overflow-hidden"
            style={{ width: `${completionPercentage}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-2 text-xs text-white/55">
          <span>{userProgress?.completed} completed</span>
          <span>{userProgress?.total} total resources</span>
        </div>
      </div>
      {/* Progress Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {progressStats?.map((stat) => (
          <div key={stat?.id} className="text-center">
            <div className={`w-12 h-12 rounded-2xl ${stat?.bgColor} border border-white/10 flex items-center justify-center mx-auto mb-2`}>
              <Icon name={stat?.icon} size={20} className={stat?.color} />
            </div>
            
            <div className="text-xl font-bold text-white mb-1">
              {stat?.value}
              {stat?.unit && <span className="text-sm text-white/60 ml-1">{stat?.unit}</span>}
            </div>
            
            <div className="text-xs text-white/60">
              {stat?.label}
            </div>
          </div>
        ))}
      </div>
      {/* Weekly Goal */}
      <div className="mt-6 pt-6 border-t border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={16} className="text-white/70" />
            <span className="text-sm font-medium text-white/80">Weekly Goal</span>
          </div>
          <span className="text-xs text-white/60">
            {userProgress?.weeklyCompleted || 0} / {userProgress?.weeklyGoal || 5}
          </span>
        </div>
        
        <div className="w-full bg-white/5 rounded-full h-2 border border-white/10 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${Math.min(100, ((userProgress?.weeklyCompleted || 0) / (userProgress?.weeklyGoal || 5)) * 100)}%` 
            }}
          />
        </div>
      </div>
      {/* Achievement Badges */}
      {userProgress?.recentAchievements && userProgress?.recentAchievements?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Award" size={16} className="text-white/70" />
            <span className="text-sm font-medium text-white/80">Recent Achievements</span>
          </div>
          
          <div className="flex items-center space-x-2 overflow-x-auto scrollbar-hide">
            {userProgress?.recentAchievements?.map((achievement, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 px-3 py-2 bg-white/5 border border-white/10 rounded-full whitespace-nowrap"
              >
                <Icon name="Star" size={14} className="text-[#8B7CFF]" fill="currentColor" />
                <span className="text-xs font-medium text-white/80">{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
